// src/utils/numeroLab.js

/**
 * Entrada numérica de resultado de laboratório, em pt-BR.
 *
 * POR QUE EXISTE: o laboratorista digita contagens de 6 a 9 dígitos
 * (920000000) — já saiu resultado com três zeros a mais sem nada na tela para
 * perceber. Agrupando em milhares enquanto se digita, `920.000` e `920.000.000`
 * ficam visivelmente diferentes.
 *
 * DECIMAL EM TODAS AS UNIDADES, de propósito. Contagens baixas têm casa
 * decimal — UFC como 2,4, e também NMP da cartela (Quanti-Tray), cuja tabela dá
 * uma casa nas contagens baixas. Não existe regra "unidade X aceita decimal":
 * uma regra por unidade exigiria mexer no CRM a cada unidade nova cadastrada no
 * protocolo. Quem digita um inteiro continua salvando um inteiro; a vírgula só
 * entra se for digitada.
 *
 * Formato: ponto agrupa milhar, vírgula separa decimal (pt-BR).
 */

/** Máximo de casas decimais aceitas no campo. */
export const CASAS_DECIMAIS_LAB = 2;

/** Agrupa uma sequência de dígitos em milhares, sem passar por Number (exato). */
const agruparMilhar = (digitos) =>
    digitos.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Máscara aplicada a cada tecla: entra o texto do campo, sai o texto formatado.
 *
 * Mantém uma vírgula no fim enquanto se digita ("2," → "2,"), para dar tempo de
 * escrever a casa decimal.
 */
export const formatarValorLab = (texto) => {
    let t = String(texto ?? '');

    // No celular o teclado numérico costuma oferecer só o PONTO. A máscara nunca
    // deixa o texto terminando em ponto (ela só põe ponto ENTRE grupos de milhar),
    // então um ponto no fim só pode ter sido digitado agora — e é intenção decimal.
    if (t.endsWith('.') && !t.includes(',')) t = `${t.slice(0, -1)},`;

    const [parteInteira, ...resto] = t.split(',');
    const temVirgula = resto.length > 0;
    const inteiro = parteInteira.replace(/\D/g, '');
    const decimal = resto
        .join('')
        .replace(/\D/g, '')
        .slice(0, CASAS_DECIMAIS_LAB);

    if (inteiro === '' && !temVirgula) return '';
    const inteiroFmt = inteiro === '' ? '0' : agruparMilhar(inteiro);
    return temVirgula ? `${inteiroFmt},${decimal}` : inteiroFmt;
};

/**
 * Valor a persistir: número exato, ou `null` quando o campo está vazio.
 *
 * Remove TODOS os pontos de milhar antes de trocar a vírgula — o antigo
 * `cleanAndParse` trocava só o primeiro separador e salvava "920.000" como 920.
 */
export const parseValorLab = (texto) => {
    const t = String(texto ?? '').trim();
    if (t === '') return null;
    const n = Number(t.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Texto do campo a partir de um valor já persistido (mesmo formato da máscara). */
export const valorParaTexto = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    if (typeof valor === 'string') return formatarValorLab(valor);
    return new Intl.NumberFormat('pt-BR', {
        maximumFractionDigits: CASAS_DECIMAIS_LAB,
    }).format(valor);
};

/**
 * Exibição do valor do laboratório no card.
 *
 * Separado do `toScientificResult` do card porque aquele também formata a
 * predição da IA, e lá zero casas é o correto. Aqui 2,4 precisa aparecer como
 * "2,4", não "2". Acima de 1 milhão segue a notação científica do card.
 */
export const formatarValorLabExibicao = (valor) => {
    const n = typeof valor === 'number' ? valor : parseValorLab(valor);
    if (n === null || !Number.isFinite(n)) return '—';
    if (n === 0) return '0';
    if (Math.abs(n) < 1000000) {
        return new Intl.NumberFormat('pt-BR', {
            maximumFractionDigits: CASAS_DECIMAIS_LAB,
        }).format(n);
    }
    return new Intl.NumberFormat('pt-BR', {
        notation: 'scientific',
        maximumFractionDigits: 0,
    })
        .format(n)
        .toUpperCase();
};

/**
 * A unidade é comparável com a predição do modelo?
 *
 * O modelo produz UM número por bactéria e o copia nas chaves `_nmp` e `_ufc`
 * (bioailab-inference-go, predictor.go:392-395) — não existe predição de UFC, e
 * o treino foi contra laboratório em NMP (`evaluateAgainstLab` lê
 * `coliformesTotaisNmp`/`ecoliNmp`). Comparar um valor em UFC com essa predição
 * produziria uma diferença sem significado.
 *
 * `null`/vazio = registro legado, quando NMP/100mL era a única unidade da tela.
 */
export const unidadeComparavelComModelo = (unit) => {
    if (!unit) return true;
    return String(unit).trim().toUpperCase().startsWith('NMP');
};

/** Unidade assumida em registros anteriores ao campo `unit`. */
export const UNIDADE_LEGADO = 'NMP/100mL';

/**
 * Entradas comparáveis com a predição, na ordem original.
 *
 * Existe porque várias telas identificam a bactéria pela POSIÇÃO no array
 * (`[0]` = Coliformes, `[1]` = E. coli). Desde que o labResult passou a aceitar
 * uma entrada por bactéria × unidade, indexar o array cru desalinha: num
 * protocolo com UFC e NMP, `[1]` é o NMP do Coliformes, não a E. coli.
 *
 * Filtrando pelas comparáveis, a posição volta a significar bactéria — e em
 * registro legado (sem `unit`) nada muda, porque todas são comparáveis.
 */
export const entradasComparaveis = (bacterias) =>
    (bacterias || []).filter((b) => unidadeComparavelComModelo(b?.unit));
